import { useEffect, useState } from "react";
import { useQuickStartStore } from "@/stores/useQuickStartStore";
import { useOnboardingStore } from "@/stores/useOnboardingStore";
import { useGuidesStore } from "@/stores/useGuidesStore";
import { useTourState, type TourVariant } from "@/hooks/useTourState";
import { useUnreadAnnouncements } from "@/hooks/useAnnouncements";
import { WelcomeModal } from "./WelcomeModal";
import { TourStep } from "./TourStep";
import { SuggestionCard } from "@/components/suggestion/SuggestionCard";

interface TourStepDef {
  /** Omit for a centered, un-anchored step (no spotlight). */
  targetSelector?: string;
  title: string;
  body: string;
  primaryLabel: string;
  side?: "top" | "right" | "bottom" | "left";
  /** Render a non-interactive demo nudge card (bottom-right) for this step so
   *  the user sees a real example instead of imagining it. */
  mockNudge?: boolean;
}

const NEW_USER_WELCOME = {
  title: "Welcome to PaperChase",
  body: "PaperChase has built-in guidance for every feature. Quick tour of where to find it?",
  primaryLabel: "Show me",
};

const NEW_USER_STEPS: TourStepDef[] = [
  {
    targetSelector: '[data-tour-target="workflow-tabs"]',
    title: "Your research, in four stages",
    body:
      "Concepts → Curate → Analyze → Build. Curate gathers and scores papers, Analyze maps the field's debate, Build stakes your claim with evidence.",
    primaryLabel: "Next",
    side: "bottom",
  },
  {
    // Prefer the rich seeding panel (new-user flow); fall back to the always-present
    // Seed Papers panel (expert/replay) so the step always has a target to spotlight.
    targetSelector:
      '[data-tour-target="corpus-seeding-panel"], [data-tour-target="seed-papers-panel"]',
    title: "Start here",
    body:
      "PaperChase can discover seed papers for you, import a .bib file, or sync with Zotero. Pick any path — you can change later.",
    primaryLabel: "Next",
    side: "top",
  },
  {
    targetSelector: '[data-tour-target="whats-next"]',
    title: "Lost? Open the map",
    body: "This shows where you are and what's next. Open it anytime from “What's next?” in the sidebar.",
    primaryLabel: "Next",
    side: "right",
  },
  {
    targetSelector: '[data-tour-target="guides-catalog-button"]',
    title: "Deeper guides live here",
    body:
      "Full walkthroughs for every feature — and you can replay this tour anytime.",
    primaryLabel: "Next",
    side: "bottom",
  },
  {
    // Anchored to a live demo nudge so the user sees what guidance looks like.
    targetSelector: '[data-tour-target="mock-nudge"]',
    mockNudge: true,
    side: "top",
    title: "We'll guide you as you go",
    body:
      "Like this card — when there's a clear next step, PaperChase drops a nudge in the bottom-right. Open it for the full map and ranking, or dismiss it. More tools unlock as your corpus grows.",
    primaryLabel: "Got it",
  },
];

const SETUP_COMPLETE_WELCOME = {
  title: "You're set up",
  body: "Your seeds are being trawled and the papers in your range graded now. Quick look at where things are?",
  primaryLabel: "Show me",
};

const SETUP_COMPLETE_STEPS: TourStepDef[] = [
  {
    targetSelector: '[data-tour-target="workflow-tabs"]',
    title: "Running now",
    body: "Curate is filling in: scores land as they're graded. Analyze and Build open up as your corpus grows.",
    primaryLabel: "Next",
    side: "bottom",
  },
  {
    targetSelector: '[data-tour-target="whats-next"]',
    title: "What's next",
    body: "Open this anytime for where you are and the next useful step.",
    primaryLabel: "Got it",
    side: "right",
  },
];

function stepsFor(
  variant: TourVariant,
): { welcome: typeof NEW_USER_WELCOME; steps: TourStepDef[] } | null {
  if (variant === "new-user") return { welcome: NEW_USER_WELCOME, steps: NEW_USER_STEPS };
  if (variant === "setup-complete")
    return { welcome: SETUP_COMPLETE_WELCOME, steps: SETUP_COMPLETE_STEPS };
  return null;
}

export function QuickStartTour() {
  const { shouldShow, variant, markSeen } = useTourState();
  const consumeJustCreated = useQuickStartStore((s) => s.consumeJustCreated);
  const clearReplayOverride = useQuickStartStore((s) => s.clearReplayOverride);
  // Watch guide UI state directly for mutual-exclusion (independent of
  // useTourState's derived variant, which can flip to "none" when the
  // trigger is consumed — see note on the effect below).
  const catalogOpen = useGuidesStore((s) => s.catalogOpen);
  const openWalkthroughId = useGuidesStore((s) => s.openWalkthroughId);

  // Defer the tour while there's a visible announcement modal pending.
  // Without this, the announcement renders on first paint and then the
  // tour's start-effect (running on the next tick) suppresses it via
  // the four-way mutex in AnnouncementModal. Net UX bug: announcement
  // flickers in then disappears before the user can read it. We treat
  // `data === undefined` (still loading) as "wait" so the announcement
  // query resolves before we decide.
  const { data: unreadAnnouncements, isLoading: announcementsLoading } =
    useUnreadAnnouncements();
  const hasUnreadAnnouncement = (unreadAnnouncements?.length ?? 0) > 0;
  const announcementsReady =
    !announcementsLoading && !hasUnreadAnnouncement;

  // Local orchestrator state
  const [activeVariant, setActiveVariant] = useState<TourVariant>("none");
  const [stepIndex, setStepIndex] = useState(0); // 0 = welcome modal, 1..N = TourStep

  // Start a tour when eligible AND no unread announcements are blocking
  // the dashboard. Once the user acks the announcement, the unread
  // query invalidates, this effect re-runs, and the tour starts.
  useEffect(() => {
    if (
      shouldShow &&
      variant !== "none" &&
      activeVariant === "none" &&
      announcementsReady
    ) {
      setActiveVariant(variant);
      setStepIndex(0);
      if (variant === "new-user") consumeJustCreated();
      if (variant === "setup-complete") useQuickStartStore.getState().consumeSetupCompleted();
    }
  }, [shouldShow, variant, activeVariant, announcementsReady, consumeJustCreated]);

  // Publish active state as an OnboardingStore claim — the controller
  // derives canShow.announcement from `activeSurface !== "tour"` so this
  // is now the single source of truth for tour-vs-announcement mutex.
  useEffect(() => {
    const store = useOnboardingStore.getState();
    if (activeVariant !== "none") {
      store.claim("tour");
    } else {
      store.release("tour");
    }
    return () => {
      useOnboardingStore.getState().release("tour");
    };
  }, [activeVariant]);

  // Close tour if the user opens the Guides catalog or a walkthrough mid-tour.
  // We subscribe to useGuidesStore directly rather than relying on the derived
  // `shouldShow`/`variant` from useTourState, because the new-user trigger
  // (`conceptJustCreated`) is consumed on start, which immediately flips
  // `variant` to "none" — that is not a mutual-exclusion event.
  useEffect(() => {
    if (activeVariant !== "none" && (catalogOpen || openWalkthroughId !== null)) {
      closeTour(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogOpen, openWalkthroughId]);

  // Cross-tab: listen for storage event → if another tab marked seen, close this tour
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (
        e.key &&
        e.key.startsWith("paperchase-quickstart-seen-") &&
        e.newValue === "1" &&
        activeVariant !== "none"
      ) {
        closeTour(false);
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant]);

  function closeTour(mark: boolean) {
    if (mark) markSeen();
    clearReplayOverride();
    setActiveVariant("none");
    setStepIndex(0);
  }

  const content = stepsFor(activeVariant);
  if (!content) return null;

  const { welcome, steps } = content;

  // Step 0 = welcome modal (no counter — counter starts at the first spotlight)
  if (stepIndex === 0) {
    return (
      <WelcomeModal
        open
        title={welcome.title}
        body={welcome.body}
        primaryLabel={welcome.primaryLabel}
        onPrimary={() => setStepIndex(1)}
        onSkip={() => closeTour(true)}
      />
    );
  }

  // Step 1..N = TourStep. stepIndex is 1-indexed into the spotlight `steps` array,
  // but we render it as step (stepIndex + 1) of (steps.length + 1) so the welcome
  // modal counts as step 1 in the user-visible counter.
  const stepDef = steps[stepIndex - 1];
  if (!stepDef) {
    closeTour(true);
    return null;
  }

  const isLast = stepIndex === steps.length;
  return (
    <>
      {stepDef.mockNudge && (
        // Demo-only nudge: a fixed wrapper supplies the real bottom-right
        // placement while the inner card stays in-flow, so the spotlight's
        // `position: relative` doesn't dislodge it. Handlers are no-ops — the
        // step's "Got it"/click-through drives the tour.
        <div className="fixed bottom-5 right-5 z-40">
          <SuggestionCard
            floating={false}
            data-tour-target="mock-nudge"
            title="Trawl from your seeds"
            body="Your seed papers are set — trawling expands them into a corpus and scores it automatically."
            ctaLabel="See why & alternatives"
            onCta={() => {}}
            onDismiss={() => {}}
          />
        </div>
      )}
      <TourStep
        key={stepIndex}
        targetSelector={stepDef.targetSelector}
        stepNumber={stepIndex}
        totalSteps={steps.length}
        title={stepDef.title}
        body={stepDef.body}
        primaryLabel={stepDef.primaryLabel}
        side={stepDef.side}
        onPrimary={() => {
          if (isLast) closeTour(true);
          else setStepIndex(stepIndex + 1);
        }}
        onSkip={() => closeTour(true)}
      />
    </>
  );
}
